// @ts-check
import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import { getEntryByUid } from './CsApi';
import { IsLivePreviewEnabled, onEntryChange, unsubscribe } from './CsSdk';
import { LivePreview } from './livePreview/LivePreviewState';
import debounce from 'lodash.debounce';

/**
 * @typedef UseContentStackPropsConfig
 * @property {boolean} [enableLivePreviewUpdate=false] A prop indicating whether the content should be fetched from the SDK in reaction to LivePreview, or just once.
 * @property {boolean} [fetchDynamicData=false] A prop indicating whether the content should be fetched on refresh. Only used for Alerts.
 * @property {boolean} [isOptional=false] A value indicating whether missing content is allowed. If so, null is returned.
 */

/**
 * @function UseContentStackProps
 * @template {import('./types').BaseT} Props The generic type of the model to use.
 * @param {import('./types').UsePreviewOnModelOptions<Props>} options
 * @param {UseContentStackPropsConfig} [config]
 * @returns {[Props, import('./types').ModelWithLivePreviewAttr<Props>, import('./livePreview/LivePreviewState').LivePreviewState<Props>]}
 */
export function useContentStackProps(options, config) {
	// Spread options first.
	const { staticModel, contentType, contentUid, referenceFieldPaths = [], locale = 'en-us' } = cloneDeep(options);
	const { enableLivePreviewUpdate = true, fetchDynamicData = false, isOptional = false } = config ?? {};

	// If we are allowing optional, this is not a big deal.
	if (!isOptional && (!contentUid || !staticModel?.uid)) {
		// This should always be present in static and dynamic models.
		// If this is missing, it hints more towards a missing model in the given environment.
		throw new Error(`Missing Content Uid for ${contentType}. Check it's deployment environment in ContentStack`);
	}

	// Set up the state to hold the props.
	/** @type {import('./livePreview/LivePreviewState').LivePreviewState<Props>} */
	const initialState = LivePreview.Create({ staticModel: staticModel, previewModel: staticModel, contentType, locale });
	const [state, setState] = React.useState(initialState);

	let [isHookMounted, setIsHookMounted] = React.useState(true);
	let [livePreviewCallbackId, setCallbackId] = React.useState('');

	// Run useEffect to perform request on mount.
	const onChangeEffectHandler = debounce(() => {
		if (!ValidateParams(contentType, contentUid, isOptional)) {
			return;
		}

		const getEntryOptions = {
			staticModel,
			contentType,
			contentUid,
			referenceFieldPaths,
		};

		if (fetchDynamicData) {
			getEntryByUid(getEntryOptions).then((res) => HandleSuccessSafely(res, isHookMounted, setState));
		} else if (!IsLivePreviewEnabled) {
			// No live preview, no need to update our state - staticModel only.
			return;
		} else if (!enableLivePreviewUpdate) {
			getEntryByUid(getEntryOptions).then((res) => HandleSuccessSafely(res, isHookMounted, setState));
		} else {
			// Set up the live preview listener
			const callbackId = onEntryChange(() => {
				// Within the callback, we will fetch an entry on each change.
				getEntryByUid(getEntryOptions)
					.then((res) => HandleSuccessSafely(res, isHookMounted, setState))
					.catch((err) => console.error(err));
			});
			setCallbackId(callbackId);
		}

		return () => {
			setIsHookMounted(false);

			if (livePreviewCallbackId) {
				unsubscribe(livePreviewCallbackId);
			}
		};
	}, 500);

	// We don't reference the array, or we get in an infinite loop
	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(onChangeEffectHandler, [contentType, contentUid]);

	const current = LivePreview.getCurrent(state);
	return [current.currentProps, current.currentPropsWith$, state];
}

/**
 * @function ValidateParams
 * @param {string} contentType
 * @param {string} contentUid
 * @param {boolean} isOptional
 * @returns {boolean} A value indicating whether the params are valid.
 */
function ValidateParams(contentType, contentUid, isOptional) {
	const isValidContentType = contentType.length > 0 && !contentType.startsWith('DEFAULT');

	if (!isValidContentType) {
		if (!isOptional) {
			console.error('Invalid ContentStack content type:\t', contentType);
		}

		// Cannot do anything without content type.
		return false;
	}
	if (!contentUid) {
		if (!isOptional) {
			console.error('Invalid ContentStack params:\n\tcontentType:', contentType, '\n\tcontentUid:', contentUid);
		}

		// Cannot do anything without an entry uid.
		return false;
	}

	return true;
}

/**
 * @template {import('./types').BaseT} Props The generic type of the model to use.
 * @function HandleSuccessSafely
 * @param {Props} updatedProps
 * @param {boolean} isHookMounted
 * @param {import('react').Dispatch<import('react').SetStateAction<import('./livePreview/LivePreviewState').LivePreviewState<Props>>>} [setState]
 */
function HandleSuccessSafely(updatedProps, isHookMounted, setState) {
	// Component hasn't actually mounted yet - skip.
	if (!isHookMounted) {
		return;
	}

	// State has been destroyed, or got bad props - skip.
	if (!setState || !updatedProps) {
		return;
	}

	setState((prev) => LivePreview.Update(prev, updatedProps));
}

